import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../common/Layout";
import { useAuth } from "../../../contexts/AuthContext";
import LeadFilters from "../../leads/LeadFilters";
import LeadSortMenu from "../../leads/LeadSortMenu";
import { QUICK_FILTER_CHIPS } from "../../leads/QuickFiltersBar";
import reportTemplatesService from "../../../services/reportTemplatesService";
import ReportTemplateColumnsEditor from "./ReportTemplateColumnsEditor";
import ReportTemplatePreview from "./ReportTemplatePreview";
import { TEMPLATE_COLUMNS, TEMPLATE_SORT_KEYS } from "./reportColumnCatalog";

const DEFAULT_FILTERS = {
  status: "all",
  search: "",
  category: "",
  products: [],
  dmsProviders: [],
  leadType: "all",
  leadSource: "all",
  serviceDashboard: "all",
  salesRep: "all",
  nextContact: "all",
};

const DEFAULT_VISIBLE = (() => {
  const v = {};
  for (const c of TEMPLATE_COLUMNS) v[c.viewKey] = true;
  return v;
})();

const DEFAULT_ORDER = TEMPLATE_COLUMNS.map((c) => c.viewKey);

export default function ReportTemplateBuilder() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const { user } = useAuth();
  const userRole = user?.role || "";
  const isEdit = Boolean(routeId);

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [headerText, setHeaderText] = useState("");
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [multiValueColumnMode, setMultiValueColumnMode] = useState("all");
  const [sortState, setSortState] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);
  const [columnOrder, setColumnOrder] = useState(DEFAULT_ORDER);
  const [columnLabels, setColumnLabels] = useState({});
  const [activeQuickChips, setActiveQuickChips] = useState([]);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const tpl = await reportTemplatesService.get(routeId);
        if (!tpl || cancelled) return;
        const p = tpl.payload || {};
        setName(tpl.name || "");
        setTitle(p.title || "");
        setHeaderText(p.headerText || "");
        setFilters((cur) => ({ ...cur, ...(p.filters || {}) }));
        setSortState(Array.isArray(p.sortState) ? p.sortState : []);
        setVisibleColumns(
          p.visibleColumns && typeof p.visibleColumns === "object"
            ? { ...DEFAULT_VISIBLE, ...p.visibleColumns }
            : DEFAULT_VISIBLE,
        );
        setColumnOrder(
          Array.isArray(p.columnOrder) && p.columnOrder.length
            ? p.columnOrder
            : DEFAULT_ORDER,
        );
        setColumnLabels(
          p.columnLabels && typeof p.columnLabels === "object"
            ? p.columnLabels
            : {},
        );
        setActiveQuickChips(
          Array.isArray(p.activeQuickChips) ? p.activeQuickChips : [],
        );
        setMultiValueColumnMode(p.filters?.multiValueColumnMode || "all");
      } catch {
        if (!cancelled) setError("Failed to load template");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, routeId]);

  const buildPayload = useCallback(() => {
    const order = columnOrder.filter((k) =>
      TEMPLATE_COLUMNS.some((c) => c.viewKey === k),
    );
    const vc = {};
    for (const k of order) {
      if (visibleColumns[k] !== false) vc[k] = true;
    }
        return {
      filters: {
        ...filters,
        multiValueColumnMode: multiValueColumnMode || "all",
      },
      sortState,
      visibleColumns: vc,
      columnOrder: order,
      columnLabels,
      title: title.trim(),
      headerText: headerText.trim(),
      activeQuickChips,
      density: "comfortable",
    };
  }, [
    filters,
    sortState,
    visibleColumns,
    columnOrder,
    columnLabels,
    title,
    headerText,
    activeQuickChips,
    multiValueColumnMode,   // <-- NEW
  ]);

  const previewPayload = useMemo(() => buildPayload(), [buildPayload]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = buildPayload();
      if (isEdit) {
        await reportTemplatesService.update(routeId, {
          name: name.trim(),
          payload,
        });
      } else {
        await reportTemplatesService.create({
          name: name.trim(),
          payload,
        });
      }
      setFlash("Template saved");
      setTimeout(() => navigate("/reports"), 400);
    } catch {
      setError("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickChipToggle = (chip) => {
    setActiveQuickChips((cur) => {
      const isOn = cur.includes(chip.key);
      const next = isOn
        ? cur.filter((k) => k !== chip.key)
        : [...cur, chip.key];
      setFilters((f) => {
        const merged = { ...f };
        for (const [k, v] of Object.entries(chip.filter)) {
          merged[k] = isOn ? "all" : v;
        }
        return merged;
      });
      return next;
    });
  };

  if (loading) {
    return (
      <Layout title="Report Template">
        <div className="r6-tpl-page">Loading template…</div>
      </Layout>
    );
  }

  return (
    <Layout
      title={isEdit ? "Edit Report Template" : "New Report Template"}
      subtitle="Define columns, friendly names, and filters. Reports can use this template."
    >
      <div className="r6-tpl-page">
        <div className="r6-tpl-page-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate("/reports")}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" /> Saving…
              </>
            ) : (
              <>
                <i className="fas fa-save" /> Save Template
              </>
            )}
          </button>
        </div>

        {flash && (
          <div className="r6-tpl-flash">
            <i className="fas fa-check-circle" /> {flash}
          </div>
        )}
        {error && (
          <div className="error-message" role="alert">
            <i className="fas fa-exclamation-circle" /> {error}
          </div>
        )}

        <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Basics</div>
          <div className="r6-tpl-grid-2">
            <div className="form-group">
              <label>
                Template name <span className="r6-req">*</span>
              </label>
              <div className="r6-tpl-name-wrap">
                <input
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Q3 Won Deals"
                  /* No maxLength — server truncates to 120 with proper ellipsis handling */
                />
                <div
                  className={`r6-tpl-name-counter ${
                    name.length > 120 ? "is-over" : ""
                  }`}
                >
                  {name.length} / 120
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Report title (optional)</label>
              <input
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Won Deals — Q3 2026"
                maxLength={200}
              />
            </div>
            <div className="form-group r6-tpl-span-2">
              <label>Header line (optional)</label>
              <input
                className="form-control"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                placeholder="Prepared for leadership review"
                maxLength={200}
              />
            </div>
          </div>
        </section>

        <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Columns</div>
          <p className="r6-tpl-section-sub">
            Toggle columns, rename them for the report, and reorder with the arrows.
          </p>
          <ReportTemplateColumnsEditor
            columnOrder={columnOrder}
            visibleColumns={visibleColumns}
            columnLabels={columnLabels}
            onChange={({
              columnOrder: co,
              visibleColumns: vc,
              columnLabels: cl,
            }) => {
              if (co) setColumnOrder(co);
              if (vc) setVisibleColumns(vc);
              if (cl) setColumnLabels(cl);
            }}
          />
        </section>

        <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Quick filters</div>
          <p className="r6-tpl-section-sub">
            Common filters are stored with the template and applied when the report runs.
          </p>
          <div className="r6-quick-panel">
            {QUICK_FILTER_CHIPS.map((chip) => {
              const active = activeQuickChips.includes(chip.key);
              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`r6-quick-chip ${active ? "is-active" : ""}`}
                  onClick={() => handleQuickChipToggle(chip)}
                >
                  {chip.label}
                  {active && <i className="fas fa-times r6-quick-chip-x" />}
                </button>
              );
            })}
          </div>
        </section>

        <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Advanced filters</div>
          <p className="r6-tpl-section-sub">
            Every filter is stored with the template and applied when the report runs.
          </p>
          <LeadFilters
            onFilterChange={(next) =>
              setFilters((cur) => ({ ...cur, ...next }))
            }
            activeStatus={filters.status}
            userRole={userRole}
            externalFilters={filters}
          />
        </section>

        <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Sort</div>
          <p className="r6-tpl-section-sub">
            Click a column to cycle asc → desc → off. Shift+click adds secondary sorts.
          </p>
          <LeadSortMenu
            sortState={sortState}
            onSortChange={setSortState}
            columns={TEMPLATE_SORT_KEYS}
          />
        </section>
                <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Multi-value columns display</div>
          <p className="r6-tpl-section-sub">
            Products and DMS Providers — control what shows in those columns.
          </p>

          <div className="r6-mv-mode">
            <label className="r6-mv-mode-option">
              <input
                type="radio"
                name="mvModeBuilder"
                value="all"
                checked={multiValueColumnMode === "all"}
                onChange={(e) => setMultiValueColumnMode(e.target.value)}
              />
              <div>
                <div className="r6-mv-mode-label">
                  Show all values on the lead
                </div>
                <div className="r6-mv-mode-hint">
                  Every product and DMS on each lead — full context.
                </div>
              </div>
            </label>

            <label className="r6-mv-mode-option">
              <input
                type="radio"
                name="mvModeBuilder"
                value="filtered"
                checked={multiValueColumnMode === "filtered"}
                onChange={(e) => setMultiValueColumnMode(e.target.value)}
              />
              <div>
                <div className="r6-mv-mode-label">
                  Show only values matching the filter
                </div>
                <div className="r6-mv-mode-hint">
                  Trim each column to just the values you filtered by.
                </div>
              </div>
            </label>

            {multiValueColumnMode === "filtered" && (
              <div className="r6-mv-mode-info">
                <i className="fas fa-info-circle" />
                <span>
                  Only applies to columns whose filter has values.
                </span>
              </div>
            )}
          </div>
        </section>
        <section className="r6-tpl-section">
          <div className="r6-tpl-section-title">Preview</div>
          <p className="r6-tpl-section-sub">
            Live preview of the first 50 matching rows using your current filters.
          </p>
          <ReportTemplatePreview
            payload={previewPayload}
            filters={previewPayload.filters}
            title={title}
            headerText={headerText}
          />
        </section>
      </div>
    </Layout>
  );
}